<div class="flex flex-col h-full bg-white dark:bg-gray-900">

  <!-- Toolbar / breadcrumbs -->
  <div class="flex items-center gap-2 px-6 py-3 border-b border-gray-200 dark:border-gray-800 text-sm text-gray-500 dark:text-gray-400">
    @for (crumb of breadcrumbs; track crumb.prefix; let last = $last) {
      @if (!last) {
        <button (click)="navigateToFolder(crumb.prefix)"
          class="hover:text-gray-900 dark:hover:text-white transition-colors">
          {{ crumb.label }}
        </button>
        <span class="text-gray-300 dark:text-gray-600">/</span>
      } @else {
        <span class="text-gray-900 dark:text-white font-medium">{{ crumb.label }}</span>
      }
    }
    <div class="flex-1"></div>
    <button
      (click)="load(currentPrefix)"
      [disabled]="loading"
      class="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
      title="Refresh">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
      </svg>
    </button>
  </div>

  <!-- Content -->
  <div class="flex-1 overflow-y-auto px-6 py-4">

    <!-- Loading -->
    @if (loading) {
      <div class="flex items-center justify-center h-40 text-gray-400 dark:text-gray-600 text-sm">
        Loading…
      </div>
    }

    <!-- Error -->
    @if (!loading && loadError) {
      <div class="text-red-500 text-sm p-4">{{ loadError }}</div>
    }

    <!-- Empty -->
    @if (!loading && !loadError && folders.length === 0 && files.length === 0) {
      <div class="flex items-center justify-center h-40 text-gray-400 dark:text-gray-600 text-sm">
        No parsed files found in this location.
      </div>
    }

    <!-- Folders -->
    @for (folder of folders; track folder) {
      <button
        (click)="navigateToFolder(folder)"
        class="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group">
        <!-- Folder icon -->
        <svg class="w-5 h-5 text-yellow-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"/>
        </svg>
        <span class="text-sm text-gray-800 dark:text-gray-200">{{ folderName(folder) }}</span>
      </button>
    }

    <!-- Files -->
    @for (file of files; track file.key) {
      <div class="border border-gray-200 dark:border-gray-700 rounded-lg mb-2 overflow-hidden">

        <!-- File row -->
        <button
          (click)="toggleExpand(file)"
          class="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">

          <!-- Chevron -->
          <svg
            class="w-4 h-4 text-gray-400 flex-shrink-0 transition-transform"
            [class.rotate-90]="isExpanded(file.key)"
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>

          <!-- File icon -->
          <svg class="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>

          <span class="flex-1 text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
            {{ fileName(file.key) }}
          </span>
          <span class="text-xs text-gray-400 dark:text-gray-500 ml-2 flex-shrink-0">
            {{ formatSize(file.size) }}
          </span>
          <span class="text-xs text-gray-400 dark:text-gray-500 ml-3 flex-shrink-0">
            {{ formatDate(file.lastModified) }}
          </span>
        </button>

        <!-- Versions panel -->
        @if (isExpanded(file.key)) {
          @let panel = getExpanded(file.key);
          <div class="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">

            @if (panel?.loading) {
              <div class="px-6 py-4 text-xs text-gray-400">Loading versions…</div>
            }

            @if (panel?.error) {
              <div class="px-6 py-3 text-xs text-red-500">{{ panel!.error }}</div>
            }

            @if (!panel?.loading && !panel?.error && panel?.versions?.length === 0) {
              <div class="px-6 py-3 text-xs text-gray-400">No versions found.</div>
            }

            @for (ver of panel?.versions ?? []; track ver.versionId) {
              <div class="flex items-center gap-3 px-6 py-2.5 border-b border-gray-100 dark:border-gray-700 last:border-0">

                <!-- Latest badge -->
                @if (ver.isLatest) {
                  <span class="text-[10px] font-semibold uppercase tracking-wide bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded px-1.5 py-0.5 flex-shrink-0">
                    Latest
                  </span>
                } @else {
                  <span class="w-[44px] flex-shrink-0"></span>
                }

                <!-- Date -->
                <span class="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 w-36">
                  {{ formatDate(ver.lastModified) }}
                </span>

                <!-- Confidence -->
                <span class="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 w-20">
                  {{ formatConfidence(ver.parseConfidence) }}
                </span>

                <!-- Approval badge -->
                <span class="text-[11px] font-medium px-2 py-0.5 rounded-full flex-shrink-0"
                  [ngClass]="approvalClasses(ver.status)">
                  {{ approvalLabel(ver.status) }}
                </span>

                <!-- Approved by -->
                @if (ver.approvedBy) {
                  <span class="text-xs text-gray-400 dark:text-gray-500 truncate">
                    by {{ ver.approvedBy }}
                  </span>
                }

                <div class="flex-1"></div>

                <!-- Review action -->
                <button
                  (click)="openReview(file.key, ver.versionId)"
                  class="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 flex-shrink-0">
                  Review
                </button>
              </div>
            }
          </div>
        }

      </div>
    }

  </div>
</div>

<!-- Review dialog -->
@if (reviewOpen && reviewFile) {
  <app-review-dialog
    [parsedS3Key]="reviewFile.parsedS3Key"
    [versionId]="reviewFile.versionId"
    (close)="closeReview()"
    (saved)="onRevisionSaved($event)">
  </app-review-dialog>
}
